/**
 * Sefaria Cache Service
 *
 * Provides comprehensive caching for Sefaria API data with:
 * - Cache-first architecture
 * - Request deduplication (prevents concurrent duplicate API calls)
 * - TTL-based expiration
 * - Stale-while-revalidate pattern
 * - Offline support
 *
 * Design Principle: All API calls should go through this cache layer.
 */

import {
  CACHE_TTL,
  CACHE_VERSION,
  type SefariaText,
  type SefariaLink,
  type SefariaVersion,
  type ReaderSettings,
  type CacheEntry,
} from '@/types/sefaria';

// ============================================================
// Cache Key Prefixes
// ============================================================

const CACHE_KEYS = {
  TEXT: 'sefaria_text_',
  COMMENTARY: 'sefaria_links_',
  VERSIONS: 'sefaria_versions_',
  SETTINGS: 'sefaria_settings',
  HIGHLIGHTS: 'sefaria_highlights_',
  POSITION: 'sefaria_position_',
} as const;

// ============================================================
// In-Flight Request Tracking (Deduplication)
// ============================================================

/**
 * Map of in-flight promises to prevent duplicate concurrent requests.
 * Key is the cache key, value is the pending promise.
 */
const inFlightRequests: Map<string, Promise<unknown>> = new Map();

// ============================================================
// Core Cache Operations
// ============================================================

/**
 * Get a raw cache entry from localStorage
 */
function getRawCacheEntry<T>(key: string): CacheEntry<T> | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;

    const entry = JSON.parse(raw) as CacheEntry<T>;

    // Validate cache entry structure
    if (!entry.data || !entry.fetchedAt || !entry.expiresAt) {
      return null;
    }

    // Check schema version for migrations
    if (entry.version !== CACHE_VERSION) {
      localStorage.removeItem(key);
      return null;
    }

    return entry;
  } catch {
    // Invalid JSON or other error - remove corrupted entry
    try {
      localStorage.removeItem(key);
    } catch {
      // Ignore removal errors
    }
    return null;
  }
}

/**
 * Check if a cache entry is expired
 */
function isExpired(entry: CacheEntry<unknown>): boolean {
  return new Date(entry.expiresAt).getTime() < Date.now();
}

/**
 * Set a cache entry in localStorage
 */
function setCacheEntry<T>(key: string, data: T, ttl: number): void {
  try {
    const now = new Date();
    const entry: CacheEntry<T> = {
      data,
      fetchedAt: now.toISOString(),
      expiresAt: new Date(now.getTime() + ttl).toISOString(),
      version: CACHE_VERSION,
    };
    localStorage.setItem(key, JSON.stringify(entry));
  } catch (error) {
    // Handle localStorage quota exceeded
    if (error instanceof Error && error.name === 'QuotaExceededError') {
      console.warn('localStorage quota exceeded, clearing old cache');
      cleanupExpiredCache();
      // Retry once after cleanup
      try {
        const now = new Date();
        const entry: CacheEntry<T> = {
          data,
          fetchedAt: now.toISOString(),
          expiresAt: new Date(now.getTime() + ttl).toISOString(),
          version: CACHE_VERSION,
        };
        localStorage.setItem(key, JSON.stringify(entry));
      } catch {
        console.error('Failed to cache data even after cleanup');
      }
    }
  }
}

// ============================================================
// Request Deduplication
// ============================================================

/**
 * Fetch with deduplication - prevents concurrent duplicate API calls.
 *
 * If a request for the same cache key is already in-flight,
 * returns the existing promise instead of making a new request.
 *
 * @param cacheKey - The cache key for this request
 * @param fetcher - Function that performs the actual fetch
 * @param ttl - Time-to-live for caching the result
 * @returns The fetched data
 */
export async function fetchWithDedup<T>(
  cacheKey: string,
  fetcher: () => Promise<T>,
  ttl: number
): Promise<T> {
  // Check if request already in-flight
  const existing = inFlightRequests.get(cacheKey);
  if (existing) {
    return existing as Promise<T>;
  }

  // Create the fetch promise
  const promise = fetcher()
    .then((data) => {
      // Cache the successful result
      setCacheEntry(cacheKey, data, ttl);
      return data;
    })
    .finally(() => {
      // Always clean up the in-flight tracking
      inFlightRequests.delete(cacheKey);
    });

  // Track the in-flight request
  inFlightRequests.set(cacheKey, promise);

  return promise;
}

// ============================================================
// Text Cache Operations
// ============================================================

/**
 * Get cached parsha text
 *
 * @param ref - Parsha reference (e.g., "Exodus.13.17-17.16")
 * @param options - Optional settings
 * @returns Cached text or null
 */
export function getCachedText(
  ref: string,
  options?: { ignoreExpiry?: boolean }
): SefariaText | null {
  const key = `${CACHE_KEYS.TEXT}${ref}`;
  const entry = getRawCacheEntry<SefariaText>(key);

  if (!entry) return null;

  // Return even if expired when ignoreExpiry is true (for fallback)
  if (options?.ignoreExpiry) {
    return entry.data;
  }

  // Check expiration
  if (isExpired(entry)) {
    return null;
  }

  return entry.data;
}

/**
 * Set cached parsha text
 */
export function setCachedText(ref: string, data: SefariaText): void {
  const key = `${CACHE_KEYS.TEXT}${ref}`;
  setCacheEntry(key, data, CACHE_TTL.TEXT);
}

/**
 * Get text immediately from cache (synchronous, for instant UI)
 */
export function getImmediateText(ref: string): SefariaText | null {
  return getCachedText(ref);
}

/**
 * Check if parsha text is available offline
 */
export function isTextAvailableOffline(ref: string): boolean {
  const key = `${CACHE_KEYS.TEXT}${ref}`;
  const entry = getRawCacheEntry<SefariaText>(key);
  return entry !== null;
}

// ============================================================
// Commentary Cache Operations
// ============================================================

/**
 * Get cached commentary for a verse
 */
export function getCachedCommentary(verseRef: string): SefariaLink[] | null {
  const key = `${CACHE_KEYS.COMMENTARY}${verseRef}`;
  const entry = getRawCacheEntry<SefariaLink[]>(key);

  if (!entry) return null;
  if (isExpired(entry)) return null;

  return entry.data;
}

/**
 * Set cached commentary for a verse
 */
export function setCachedCommentary(verseRef: string, data: SefariaLink[]): void {
  const key = `${CACHE_KEYS.COMMENTARY}${verseRef}`;
  setCacheEntry(key, data, CACHE_TTL.COMMENTARY);
}

// ============================================================
// Version Cache Operations
// ============================================================

/**
 * Get cached version list for a book
 */
export function getCachedVersions(book: string): SefariaVersion[] | null {
  const key = `${CACHE_KEYS.VERSIONS}${book}`;
  const entry = getRawCacheEntry<SefariaVersion[]>(key);

  if (!entry) return null;
  if (isExpired(entry)) return null;

  return entry.data;
}

/**
 * Set cached version list for a book
 */
export function setCachedVersions(book: string, data: SefariaVersion[]): void {
  const key = `${CACHE_KEYS.VERSIONS}${book}`;
  setCacheEntry(key, data, CACHE_TTL.VERSIONS);
}

// ============================================================
// User Settings (Never Expire)
// ============================================================

const DEFAULT_SETTINGS: ReaderSettings = {
  layout: 'stacked',
  fontSize: 'medium',
  selectedVersion: '',
};

/**
 * Get reader settings
 */
export function getReaderSettings(): ReaderSettings {
  try {
    const raw = localStorage.getItem(CACHE_KEYS.SETTINGS);
    if (!raw) return DEFAULT_SETTINGS;

    const settings = JSON.parse(raw) as ReaderSettings;
    return { ...DEFAULT_SETTINGS, ...settings };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

/**
 * Set reader settings (partial update)
 */
export function setReaderSettings(settings: Partial<ReaderSettings>): void {
  try {
    const current = getReaderSettings();
    const updated = { ...current, ...settings };
    localStorage.setItem(CACHE_KEYS.SETTINGS, JSON.stringify(updated));
  } catch (error) {
    console.warn('Failed to save reader settings:', error);
  }
}

// ============================================================
// User Highlights (Never Expire)
// ============================================================

/**
 * Get highlighted verses for a user
 */
export function getHighlights(userId: string): string[] {
  try {
    const key = `${CACHE_KEYS.HIGHLIGHTS}${userId}`;
    const raw = localStorage.getItem(key);
    if (!raw) return [];

    return JSON.parse(raw) as string[];
  } catch {
    return [];
  }
}

/**
 * Set highlighted verses for a user
 */
export function setHighlights(userId: string, verses: string[]): void {
  try {
    const key = `${CACHE_KEYS.HIGHLIGHTS}${userId}`;
    localStorage.setItem(key, JSON.stringify(verses));
  } catch (error) {
    console.warn('Failed to save highlights:', error);
  }
}

/**
 * Add a verse to highlights
 */
export function addHighlight(userId: string, verseRef: string): void {
  const current = getHighlights(userId);
  if (!current.includes(verseRef)) {
    setHighlights(userId, [...current, verseRef]);
  }
}

/**
 * Remove a verse from highlights
 */
export function removeHighlight(userId: string, verseRef: string): void {
  const current = getHighlights(userId);
  setHighlights(
    userId,
    current.filter((ref) => ref !== verseRef)
  );
}

/**
 * Toggle a verse highlight
 */
export function toggleHighlight(userId: string, verseRef: string): boolean {
  const current = getHighlights(userId);
  if (current.includes(verseRef)) {
    removeHighlight(userId, verseRef);
    return false;
  } else {
    addHighlight(userId, verseRef);
    return true;
  }
}

/**
 * Clear all highlights for a user
 */
export function clearHighlights(userId: string): void {
  setHighlights(userId, []);
}

// ============================================================
// Reading Position
// ============================================================

/**
 * Get the last reading position for a parsha
 */
export function getReadingPosition(parshaRef: string): string | null {
  try {
    const key = `${CACHE_KEYS.POSITION}${parshaRef}`;
    const entry = getRawCacheEntry<string>(key);

    if (!entry) return null;
    if (isExpired(entry)) return null;

    return entry.data;
  } catch {
    return null;
  }
}

/**
 * Set the reading position for a parsha
 */
export function setReadingPosition(parshaRef: string, verseRef: string): void {
  const key = `${CACHE_KEYS.POSITION}${parshaRef}`;
  setCacheEntry(key, verseRef, CACHE_TTL.POSITION);
}

/**
 * Clear the reading position for a parsha
 */
export function clearReadingPosition(parshaRef: string): void {
  try {
    const key = `${CACHE_KEYS.POSITION}${parshaRef}`;
    localStorage.removeItem(key);
  } catch {
    // Ignore errors
  }
}

// ============================================================
// Cache Maintenance
// ============================================================

/**
 * Get all Sefaria-related localStorage keys
 */
export function getAllSefariaKeys(): string[] {
  const keys: string[] = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith('sefaria_')) {
        keys.push(key);
      }
    }
  } catch {
    // Ignore errors
  }
  return keys;
}

/**
 * Clean up expired cache entries
 * Should be called on app startup
 */
export function cleanupExpiredCache(): void {
  const keys = getAllSefariaKeys();
  const now = Date.now();

  for (const key of keys) {
    try {
      // Skip non-expiring keys
      if (key === CACHE_KEYS.SETTINGS || key.startsWith(CACHE_KEYS.HIGHLIGHTS)) {
        continue;
      }

      const raw = localStorage.getItem(key);
      if (!raw) continue;

      const entry = JSON.parse(raw) as CacheEntry<unknown>;
      if (entry.expiresAt && new Date(entry.expiresAt).getTime() < now) {
        localStorage.removeItem(key);
      }
    } catch {
      // Remove corrupted entries
      try {
        localStorage.removeItem(key);
      } catch {
        // Ignore
      }
    }
  }
}

/**
 * Clear all Sefaria cache data
 */
export function clearAllCache(): void {
  const keys = getAllSefariaKeys();
  for (const key of keys) {
    try {
      localStorage.removeItem(key);
    } catch {
      // Ignore errors
    }
  }
}

/**
 * Get cache statistics (for debugging)
 */
export function getCacheStats(): {
  totalEntries: number;
  textEntries: number;
  commentaryEntries: number;
  expiredEntries: number;
} {
  const keys = getAllSefariaKeys();
  const now = Date.now();

  let textEntries = 0;
  let commentaryEntries = 0;
  let expiredEntries = 0;

  for (const key of keys) {
    if (key.startsWith(CACHE_KEYS.TEXT)) {
      textEntries++;
    } else if (key.startsWith(CACHE_KEYS.COMMENTARY)) {
      commentaryEntries++;
    }

    try {
      const raw = localStorage.getItem(key);
      if (raw) {
        const entry = JSON.parse(raw) as CacheEntry<unknown>;
        if (entry.expiresAt && new Date(entry.expiresAt).getTime() < now) {
          expiredEntries++;
        }
      }
    } catch {
      // Ignore
    }
  }

  return {
    totalEntries: keys.length,
    textEntries,
    commentaryEntries,
    expiredEntries,
  };
}
